import type { SegmentsOption, UseFetchSegmentsPayload } from '@openx/types/targeting/customVariables.ts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';

export const CONTEXTUAL_SEGMENTS_PAGE_SIZE = 100;

type UseContextualSegmentsPaginationProps = {
  accountId?: string;
  filters?: Pick<UseFetchSegmentsPayload, 'phrase' | 'provider_id' | 'price_cpm' | 'price_pom'>;
};

const getSegmentIdentity = (segment: SegmentsOption) => `${segment.contextual_provider ?? ''}:${segment.id}`;

const haveSameIdentities = (first: SegmentsOption[], second: SegmentsOption[]) =>
  first.length === second.length &&
  first.every((segment, index) => getSegmentIdentity(segment) === getSegmentIdentity(second[index]));

export const useContextualSegmentsPagination = ({ accountId, filters = {} }: UseContextualSegmentsPaginationProps) => {
  const { useFetchSegments } = useTargetingContext();
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<SegmentsOption[]>([]);
  const queryIdentity = JSON.stringify({ accountId, filters });
  const previousQueryIdentityRef = useRef(queryIdentity);
  const requestOffset = previousQueryIdentityRef.current === queryIdentity ? offset : 0;

  const { segments, loading, totalCount, hasNextPage } = useFetchSegments({
    account_id: accountId,
    ...filters,
    limit: CONTEXTUAL_SEGMENTS_PAGE_SIZE,
    offset: requestOffset,
  });

  useEffect(() => {
    if (previousQueryIdentityRef.current !== queryIdentity) {
      previousQueryIdentityRef.current = queryIdentity;
      setOffset(0);
      setAccumulated(!loading ? segments : []);
      return;
    }

    if (loading) return;

    setAccumulated(previous => {
      if (requestOffset === 0) {
        return haveSameIdentities(previous, segments) ? previous : segments;
      }

      const existingIds = new Set(previous.map(getSegmentIdentity));
      const nextSegments = segments.filter(segment => !existingIds.has(getSegmentIdentity(segment)));
      return nextSegments.length === 0 ? previous : [...previous, ...nextSegments];
    });
  }, [loading, queryIdentity, requestOffset, segments]);

  const handleLoadMore = useCallback(() => {
    if (loading) return;
    setOffset(previous => previous + CONTEXTUAL_SEGMENTS_PAGE_SIZE);
  }, [loading]);

  return {
    segments: accumulated,
    totalCount: totalCount ?? accumulated.length,
    loading: loading && requestOffset === 0,
    loadingMore: loading && requestOffset > 0,
    hasMore: !loading && (hasNextPage ?? segments.length === CONTEXTUAL_SEGMENTS_PAGE_SIZE),
    handleLoadMore,
  };
};
